"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { ReactNode } from "react";
import { ErrorSupportInfo } from "modules/common/components/ErrorSupportInfo";

interface ErrorLink {
  label: string;
  href: string;
}

interface ErrorContentProps {
  title?: string;
  message: string;
  reset?: () => void;
  primaryLink?: ErrorLink;
  secondaryLink?: ErrorLink;
  digest?: string;
  children?: ReactNode;
  className?: string;
  hideSupportInfo?: boolean;
}

export function ErrorContent({
  title = "Something went wrong!",
  message,
  reset,
  primaryLink,
  secondaryLink,
  digest,
  children,
  className = "h-[75dvh]",
  hideSupportInfo = false,
}: ErrorContentProps) {
  const router = useRouter();

  return (
    <div
      className={`flex flex-col items-center justify-center p-4 text-center ${className}`}
    >
      <h1 className="text-base-content mb-4 text-3xl font-bold">{title}</h1>
      <p className="text-base-content/70 mb-6 max-w-md">{message}</p>
      {children}
      {digest && (
        <div className="badge badge-neutral mx-auto mb-4">ID: {digest}</div>
      )}
      <div className="flex gap-3">
        {reset && (
          <button
            className="btn btn-primary"
            onClick={() => {
              router.refresh();
              reset();
            }}
          >
            Try Again
          </button>
        )}
        {primaryLink && (
          <Link href={primaryLink.href} className="btn btn-primary">
            {primaryLink.label}
          </Link>
        )}
        {secondaryLink && (
          <Link href={secondaryLink.href} className="btn btn-ghost">
            {secondaryLink.label}
          </Link>
        )}
      </div>
      {!hideSupportInfo && <ErrorSupportInfo className="mt-6 w-full max-w-md" />}
    </div>
  );
}
